import { z } from "zod";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import type { OrganizationSettings } from "@shyne/shared/api";
import { FormCreator, FormFieldDef } from "@/components/shared/form-creator";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Skeleton } from "@/components/ui/skeleton";
import MainLayout from "@/components/layouts";
import { useOrganizationSettings } from "./organization.hooks";
import { toast } from "sonner";
import { useCallback, useEffect, useRef } from "react";
import { isPhotoFile, PHOTO_FILE_TYPES, UNSUPPORTED_PHOTO_FILE } from "@/lib/appointment-photos";
import { optionalWebAddress } from "@/lib/web-address";
import { holdMessageOr } from "@/lib/appointment-field-actions";

const formSchema = z
  .object({
    image: z.union([z.instanceof(File), z.string()]).optional(),
    display_name: z.string().trim().min(1, "Display name is required"),
    description: z.string().optional(),
    support_email: z.string().trim().email("Invalid email"),
    sameAsSupport: z.boolean(),
    billing_email: z.string().trim().email("Invalid email").optional().or(z.literal("")),
    url: optionalWebAddress,
    phone: z.string().optional(),
    location: z.string().optional(),
    timezone: z.string().optional(),
  })
  .superRefine((data, ctx) => {
    if (!data.sameAsSupport && !data.billing_email) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["billing_email"],
        message: "Billing email is required if not same as support",
      });
    }
  });

type FormValues = z.infer<typeof formSchema>;

const billingIsSupport = (organization: OrganizationSettings) => !organization.billingEmail || organization.billingEmail === organization.supportEmail;

const formValuesOf = (organization: OrganizationSettings): FormValues => ({
  image: organization.logo ?? "",
  display_name: organization.name,
  description: organization.description ?? "",
  support_email: organization.supportEmail ?? "",
  billing_email: organization.billingEmail ?? "",
  url: organization.website ?? "",
  phone: organization.phone ?? "",
  location: organization.location ?? "",
  timezone: organization.timezone ?? "",
  sameAsSupport: billingIsSupport(organization),
});

const initialsOf = (name: string) =>
  name
    .split(" ")
    .map((word) => word[0])
    .join("");

export default function OrganizationPage() {
  const {
    organization,
    loading,
    saving,
    editable,
    uploadingLogo,
    handleSave,
    handleLogoUpload,
    sameAsSupport,
    setSameAsSupport,
  } = useOrganizationSettings();

  const form = useForm<FormValues>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      image: "",
      display_name: "",
      description: "",
      support_email: "",
      sameAsSupport: true,
      billing_email: "",
      url: "",
      phone: "",
      location: "",
      timezone: "",
    },
  });

  const submitting = useRef(false);

  const showSaved = useCallback(
    (saved: OrganizationSettings, keptFile?: File) => {
      form.reset({ ...formValuesOf(saved), ...(keptFile ? { image: keptFile } : {}) });
      setSameAsSupport(billingIsSupport(saved));
    },
    [form, setSameAsSupport]
  );

  useEffect(() => {
    if (organization && !submitting.current) showSaved(organization);
  }, [organization, showSaved]);

  const picked = form.watch("image");

  useEffect(() => {
    if (picked instanceof File && !isPhotoFile(picked)) form.setError("image", { message: UNSUPPORTED_PHOTO_FILE });
    else form.clearErrors("image");
  }, [picked, form]);

  const handleSubmit = form.handleSubmit(async (values) => {
    const logo = values.image instanceof File ? values.image : null;
    const unsupportedLogo = logo !== null && !isPhotoFile(logo);
    submitting.current = true;
    try {
      const saved = await handleSave({
        name: values.display_name,
        supportEmail: values.support_email,
        description: values.description ?? null,
        billingEmail: values.sameAsSupport ? values.support_email : values.billing_email ?? null,
        website: values.url ?? null,
        phone: values.phone ?? null,
        location: values.location ?? null,
        timezone: values.timezone ?? null,
      });
      if (unsupportedLogo) {
        showSaved(saved, logo);
        form.setError("image", { message: UNSUPPORTED_PHOTO_FILE });
        toast.success("Organization details saved. The logo was not changed.");
      } else {
        showSaved(logo === null ? saved : await handleLogoUpload(logo));
        toast.success("Organization details saved successfully.");
      }
    } catch (err) {
      console.error(err);
      toast.error(holdMessageOr(err, "Failed to save organization details."));
    } finally {
      submitting.current = false;
    }
  });

  const breadcrumb = [
    { name: "Settings", href: "#" },
    { name: "Organization", href: "#" },
  ];

  if (loading || !organization) {
    return (
      <MainLayout breadcrumb={breadcrumb}>
        <Skeleton className="h-16 w-16 rounded-full" />
        <Skeleton className="h-12 w-full" />
        <Skeleton className="h-40 w-full" />
      </MainLayout>
    );
  }

  const readOnly = !editable;

  const formFields: FormFieldDef[] = [
    {
      type: "file",
      name: "image",
      label: "Logo",
      description: "A new logo is saved with Save Changes.",
      accept: PHOTO_FILE_TYPES,
      hidden: readOnly,
    },
    {
      type: "text",
      name: "display_name",
      label: "Organization Display Name",
      required: true,
      disabled: readOnly,
    },
    {
      type: "email",
      name: "support_email",
      label: "Support Email (public)",
      required: true,
      disabled: readOnly,
    },
    {
      type: "switch",
      name: "sameAsSupport",
      label: "Use same email for billing",
      wrapperClassName: "flex items-center justify-start gap-8",
      disabled: readOnly,
    },
    {
      type: "email",
      name: "billing_email",
      label: "Billing Email (private)",
      required: !sameAsSupport,
      hidden: (vals) => vals.sameAsSupport,
      disabled: readOnly,
    },
    {
      type: "textarea",
      name: "description",
      label: "Description",
      disabled: readOnly,
    },
    { type: "text", name: "url", label: "Website URL", disabled: readOnly },
    { type: "text", name: "phone", label: "Phone Number", disabled: readOnly },
    { type: "text", name: "location", label: "Location", disabled: readOnly },
    { type: "text", name: "timezone", label: "Timezone", disabled: readOnly },
  ];

  return (
    <MainLayout breadcrumb={breadcrumb}>
      <div className="container mx-auto">
        <div className="flex flex-col gap-8 max-w-2xl">
          <div className="flex items-center justify-center gap-4">
            <Avatar className="h-32 w-32">
              <AvatarImage src={organization.logo ?? undefined} alt={`${organization.name} logo`} />
              <AvatarFallback>{initialsOf(organization.name)}</AvatarFallback>
            </Avatar>
            <p className="text-sm text-muted-foreground">{uploadingLogo && "Uploading new logo…"}</p>
          </div>

          {readOnly && <p className="text-sm text-muted-foreground">Only your organization's admins can change these details.</p>}

          <FormCreator
            form={form}
            onSubmit={handleSubmit}
            formDefinition={formFields}
            buttonText={saving ? "Saving…" : "Save Changes"}
            hideButtons={readOnly}
          />
        </div>
      </div>
    </MainLayout>
  );
}
